import { useState } from 'react'
import {
  Box,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Skeleton,
  Tooltip,
  IconButton,
} from '@mui/material'
import { LocalShipping, RvHookup, Inventory, LocationOff, OpenInNew } from '@mui/icons-material'
import { AssetPosition } from 'utils/test/data-mocked/assetPositions'
import { Search as SearchIcon } from '@mui/icons-material_modern'

interface AssetMapListProps {
  positions: AssetPosition[]
  onAssetSelect: (asset: AssetPosition) => void
  selectedAsset?: AssetPosition
  loading?: boolean
}

const AssetMapList = ({ positions, onAssetSelect, selectedAsset, loading }: AssetMapListProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('alphabet')

  const filteredPositions = positions.filter(
    position =>
      position.assetName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      position.label.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const sortedPositions = [...filteredPositions].sort((a, b) => {
    switch (sortBy) {
      case 'alphabet':
        return a.assetName.localeCompare(b.assetName)
      case 'daysOnSite':
        return b.daysOnSite - a.daysOnSite
      case 'assetType':
        return a.asset.localeCompare(b.asset)
      default:
        return 0
    }
  })

  const getAssetIcon = (assetType: string) => {
    switch (assetType) {
      case 'container':
        return <Inventory sx={{ color: '#1976d2' }} />
      case 'truck':
        return <LocalShipping sx={{ color: '#9c27b0' }} />
      case 'trailer':
        return <RvHookup sx={{ color: '#2e7d32' }} />
      default:
        return null
    }
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'grey.400',
        borderRadius: 2,
        backgroundColor: 'background.default',
        pt: 2,
      }}
    >
      <Box sx={{ px: 2, mb: 2, display: 'flex', gap: 2 }}>
        <TextField
          size="small"
          placeholder="Search Assets"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Sort by</InputLabel>
          <Select value={sortBy} label="Sort by" onChange={e => setSortBy(e.target.value)}>
            <MenuItem value="alphabet">Name</MenuItem>
            <MenuItem value="daysOnSite">Days on Site</MenuItem>
            <MenuItem value="assetType">Asset Type</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Divider />
      <List sx={{ flexGrow: 1, overflow: 'auto', py: 0 }}>
        {loading
          ? [...Array(3)].map((_, index) => (
              <ListItem key={index} disablePadding divider>
                <ListItemButton>
                  <Skeleton variant="rectangular" width="100%" height={40} />
                </ListItemButton>
              </ListItem>
            ))
          : sortedPositions.map(position => (
              <ListItem
                key={position.id}
                disablePadding
                divider
                sx={{
                  '&:hover .hover-button': {
                    opacity: 1,
                  },
                }}
              >
                <ListItemButton
                  selected={selectedAsset?.id === position.id}
                  onClick={() => position.coords && onAssetSelect(position)}
                  sx={{
                    cursor: position.coords ? 'pointer' : 'default',
                    opacity: position.coords ? 1 : 0.7,
                    '&:hover': {
                      backgroundColor: position.coords ? undefined : 'transparent',
                    },
                  }}
                >
                  <Box sx={{ mr: 1 }}>{getAssetIcon(position.asset)}</Box>
                  <ListItemText
                    primary={position.assetName}
                    secondary={`${position.label} - ${position.daysOnSite} days on site`}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!position.coords && (
                      <Tooltip title="Location coordinates unavailable">
                        <LocationOff sx={{ color: 'warning.main' }} />
                      </Tooltip>
                    )}
                    <Tooltip title="Open container profile">
                      <IconButton
                        className="hover-button"
                        size="small"
                        onClick={e => {
                          e.stopPropagation()
                          window.open(`/app/assets/containers/${position.assetId}/profile`, '_blank')
                        }}
                        sx={{
                          opacity: 0,
                          transition: 'opacity 0.2s',
                        }}
                      >
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
      </List>
    </Box>
  )
}

export default AssetMapList
